import React, { useState } from "react";
import { FaYoutube } from "react-icons/fa";
import { SocialAccountCard } from "./SocialAccountCard";
import { Button } from "@/components/ui/button";
import { YouTubeDataStatusDot } from "./YouTubeDataStatusDot";
import { useTranslation } from "react-i18next";
import { FaSpinner } from "react-icons/fa";
import { useCachedTier } from '@/features/account/hooks/useCachedTier';
import { AnalyticsUpgradeModal } from './AnalyticsUpgradeModal';

interface YouTubeAccount {
    id: string;
    username?: string;
    name?: string;
    profile_picture_url?: string;
    connectionId: string;
    connectionType: "youtube";
}

interface YouTubeAccountCardProps {
    account: YouTubeAccount;
    isLoading: boolean;
    onDisconnect: (connectionId: string) => void;
    onIndex?: (accountId: string, connectionId: string) => void;
    isIndexing?: Record<string, boolean>;
    currentMessageIndex?: Record<string, number>;
    indexingMessages?: string[];
}

export function YouTubeAccountCard({
    account,
    isLoading,
    onDisconnect,
    onIndex,
    isIndexing = {},
    currentMessageIndex = {},
    indexingMessages = [],
}: YouTubeAccountCardProps) {
    const { t } = useTranslation();

    const handleDisconnect = () => {
        onDisconnect(account.connectionId);
    };

    const profilePictureUrl = account.profile_picture_url
        ? (account.profile_picture_url.startsWith('http') || account.profile_picture_url.startsWith('/'))
            ? account.profile_picture_url
            : `https://${account.profile_picture_url.replace(/^https?:\/\//, '')}`
        : undefined;

    const loading = isIndexing[account.id] || false;
    const messageIdx = currentMessageIndex[account.id] || 0;
    const loadingText = indexingMessages[messageIdx] || undefined;

    const { tier } = useCachedTier();
    const isPaidTier = tier !== null && tier.toLowerCase() !== 'free';
    const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    const handleUpgrade = () => {
        setShowUpgradeModal(false);
        fetch('/api/stripe/create-checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ plan: 'pro' }),
        })
            .then(async res => res.ok ? res.json() : null)
            .then(data => { if (data?.url) window.location.href = data.url; })
            .catch(err => console.error('[YouTubeAccountCard] checkout error:', err));
    };

    const handleAnalytics = async () => {
        if (!isPaidTier) {
            setShowUpgradeModal(true);
            return;
        }
        setIsAnalyticsLoading(true);
        try {
            const response = await fetch('/api/analytics/unlock-analytics', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ connectionId: account.connectionId, username: account.username ?? account.name }),
            });
            if (!response.ok) {
                throw new Error(`Failed to unlock analytics: ${response.statusText}`);
            }
        } catch (error) {
            console.error('[YouTubeAccountCard] Analytics request failed:', error);
        } finally {
            setIsAnalyticsLoading(false);
        }
    };

    return (
        <>
            <SocialAccountCard
                title={account.name || account.username || "YouTube Channel"}
                username={account.username}
                profilePictureUrl={profilePictureUrl}
                icon={<FaYoutube />}
                isLoading={isLoading}
                variant="default"
                onDisconnect={handleDisconnect}
                statusDot={<YouTubeDataStatusDot connectionIds={[account.connectionId]} />}
            >
                {onIndex && (
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <Button
                            className="w-full sm:flex-1 min-h-10 h-auto whitespace-normal text-center leading-tight py-2"
                            variant="highlight"
                            onClick={() => onIndex(account.id, account.connectionId)}
                            disabled={loading}
                            aria-label={t('account_social.update_ai_aria')}
                        >
                            {loading ? (
                                <>
                                    <FaSpinner className="animate-spin" />
                                    {loadingText}
                                </>
                            ) : (
                                <>{t('account_social.update_ai')}</>
                            )}
                        </Button>
                        <Button
                            className="w-full sm:flex-1 min-h-10 h-auto whitespace-normal text-center leading-tight py-2"
                            variant="secondary"
                            onClick={handleAnalytics}
                            disabled={isAnalyticsLoading}
                            aria-label={t('account_social.unlock_analytics_aria')}
                        >
                            {isAnalyticsLoading ? (
                                <>
                                    <FaSpinner className="animate-spin" />
                                    {t('account_social.unlock_analytics_loading')}
                                </>
                            ) : isPaidTier ? (
                                t('account_social.unlock_analytics')
                            ) : (
                                t('account_social.unlock_analytics_locked')
                            )}
                        </Button>
                    </div>
                )}
            </SocialAccountCard>
            <AnalyticsUpgradeModal
                isOpen={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                onUpgrade={handleUpgrade}
            />
        </>
    );
}
